import { useCallback, useMemo } from 'react';
import { useUpdateThemeMutation } from './useUpdateThemeMutation';
import type { ThemePreferenceType } from '@/styles/types';

export interface ThemeSync {
  commit: (next: ThemePreferenceType) => void;
  retry: () => void;
  isPending: boolean;
  hasError: boolean;
}

// `retry` reads `mutation.variables` so the UI doesn't need to track the failed value itself.
export function useThemeSync(currentTheme: ThemePreferenceType): ThemeSync {
  const mutation = useUpdateThemeMutation(currentTheme);
  const { mutate, variables, isPending, isError } = mutation;

  const commit = useCallback(
    (next: ThemePreferenceType) => mutate(next),
    [mutate]
  );

  const retry = useCallback(() => {
    if (variables !== undefined) {
      mutate(variables);
    }
  }, [mutate, variables]);

  return useMemo(
    () => ({ commit, retry, isPending, hasError: isError }),
    [commit, retry, isPending, isError]
  );
}
